import './CandidateRow.css';
import { useId } from 'preact/hooks';
import type { Candidate, CandidatePhoto } from '../contracts/static';
import { t } from '../copy/strings';
import { fmtInt, fmtPct } from '../format/numbers';
import { partyOf } from '../theme/parties';
import { AnimatedNumber } from './AnimatedNumber';
import { Bar } from './Bar';
import { PartyTag } from './PartyTag';
import { Portrait, type PortraitShape } from './Portrait';

/**
 * `plain` (só %), `votes` (votos inteiros sob o %), `bar` (barra de 3 px nas colunas 2–3) e `bar-votes`, a soma das
 * duas que os cartões de município e de cidade usam (§8.4, §8.5: "variante `bar` + `votes`"), §3.9.
 */
export type CandidateRowVariant = 'plain' | 'votes' | 'bar' | 'bar-votes';
/** Estado exibido (data-state): o primeiro que valer, nesta ordem. */
export type CandidateRowState = 'annulled' | 'muted' | 'empty' | 'early' | 'value';

export interface CandidateRowProps {
  /** Nome de exibição, sigla e número (Candidate, FirstRoundPerson, ArchiveCandidate…). */
  candidate: Pick<Candidate, 'display_name' | 'party_code' | 'number'>;
  /** Foto já resolvida pelo dono: `null` sem foto ou com `features.photos = false` (DE-13, como no `Portrait`). */
  photo: CandidatePhoto | null;
  /** Fatia dos válidos (0–1); `null` = sem dado ("–"). */
  share: number | null;
  /** Votos inteiros (variante `votes`); `null` = "–". */
  votes?: number | null;
  variant?: CandidateRowVariant;
  /** 28 (padrão) ou 30 (`UfCard` e cartões de lugar, §3.8). */
  portraitSize?: 28 | 30;
  portraitShape?: PortraitShape;
  /** Menos de 1% das seções: números em --color-fg-3. */
  early?: boolean;
  /** Derrotado em lista do arquivo do 1º turno. */
  muted?: boolean;
  /** Candidatura anulada (arquivo): retrato em cinza. */
  annulled?: boolean;
  /** Primeira montagem dentro da janela da intro (A02), repassado ao `AnimatedNumber`. */
  intro?: boolean;
  /** Abre o perfil (§8.6): com ele a linha é um `<button>`; sem ele, um `<div>` simples. */
  onOpen?: () => void;
  /** Classes extras do dono (ex.: `Spotlight`, poda). */
  class?: string;
}

function stateOf(p: CandidateRowProps): CandidateRowState {
  if (p.annulled) return 'annulled';
  if (p.muted) return 'muted';
  if (p.share === null) return 'empty';
  if (p.early) return 'early';
  return 'value';
}

/**
 * Linha de candidato (§3.9 `CandidateRow`): retrato, nome com reticências e `PartyTag` abaixo, valor à direita. Linha
 * sangrada (§3.5.1). Clicável, é um `<button>` com "Ver {Nome}" e o restante da linha como descrição (§15.4.2).
 */
export function CandidateRow(props: CandidateRowProps) {
  const {
    candidate,
    photo,
    share,
    votes = null,
    variant = 'plain',
    portraitSize = 28,
    portraitShape,
    muted = false,
    annulled = false,
    intro = false,
    onOpen,
    class: extra,
  } = props;
  const id = useId();
  const party = partyOf(candidate);
  const state = stateOf(props);
  const clickable = onOpen !== undefined;
  const withVotes = variant === 'votes' || variant === 'bar-votes';
  const withBar = variant === 'bar' || variant === 'bar-votes';
  const cls = [
    'candidate-row',
    variant === 'plain' && 'candidate-row--plain',
    withVotes && 'candidate-row--votes',
    withBar && 'candidate-row--bar',
    clickable && 'candidate-row--button',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
  const votesSr = (v: number) => t('candidateRow.votes', { N: v });

  const content = (
    <>
      <Portrait
        candidate={candidate}
        photo={photo}
        size={portraitSize}
        shape={portraitShape}
        muted={muted && !annulled}
        annulled={annulled}
      />
      <span class="candidate-row__main">
        <span class="candidate-row__name">{candidate.display_name}</span>
        <span class="candidate-row__tag" id={`${id}-tag`}>
          <PartyTag candidate={candidate} size="sm" />
        </span>
      </span>
      <span class="candidate-row__value" id={`${id}-value`}>
        <AnimatedNumber value={share} format={fmtPct} intro={intro} class="candidate-row__pct" />
        {withVotes && (
          <AnimatedNumber value={votes} format={fmtInt} srFormat={votesSr} intro={intro} class="candidate-row__votes" />
        )}
      </span>
      {withBar && (
        <span class="candidate-row__bar">
          <Bar segments={[{ value: share ?? 0, party }]} variant="line" />
        </span>
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        class={cls}
        data-testid="candidate-row"
        data-state={state}
        aria-label={t('candidateRow.open', { NOME: candidate.display_name })}
        aria-describedby={`${id}-tag ${id}-value`}
        onClick={() => onOpen()}
      >
        {content}
      </button>
    );
  }
  return (
    <div class={cls} data-testid="candidate-row" data-state={state}>
      {content}
    </div>
  );
}
